import { useMemo, useState } from "react";
import {
  Ruler, Storefront, ShoppingCartSimple, DoorOpen, Package,
  Car, Buildings, VideoCamera, HardDrives, CurrencyDollar, Cpu,
} from "@phosphor-icons/react";

const RES_BITRATE = { "2MP (1080p)": 2, "4MP (2K)": 4, "8MP (4K)": 8 };

function NumberField({ label, value, onChange, icon: Icon, min = 0 }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">
        <Icon size={14} weight="bold" /> {label}
      </label>
      <input type="number" min={min} value={value}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
        className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm font-mono-data outline-none focus:border-[#007AFF] transition-colors" />
    </div>
  );
}

export default function Planner() {
  const [sqft, setSqft] = useState(30000);
  const [aisles, setAisles] = useState(16);
  const [lanes, setLanes] = useState(8);
  const [entrances, setEntrances] = useState(2);
  const [stockrooms, setStockrooms] = useState(1);
  const [offices, setOffices] = useState(1);
  const [parking, setParking] = useState(true);
  const [retention, setRetention] = useState(30);
  const [res, setRes] = useState("4MP (2K)");

  const plan = useMemo(() => {
    const floorGeneral = Math.ceil(sqft / 2500); // wide-angle overhead coverage
    const rows = [
      { zone: "Sales Floor / Aisles", model: "Hikvision AcuSense Dome", unit: 160, count: aisles + floorGeneral, note: "1 per aisle + overhead coverage" },
      { zone: "Checkout Lanes", model: "Hikvision AcuSense Dome + POS", unit: 160, count: lanes, note: "1 per lane, POS-linked for fraud" },
      { zone: "Entrances", model: "Dual-lens People Counter", unit: 300, count: entrances * 2, note: "count in/out + face-level" },
      { zone: "Stock / Receiving", model: "Amcrest 4K Bullet", unit: 100, count: stockrooms * 3, note: "highest shrink point" },
      { zone: "Back Office", model: "Reolink RLC-1224A", unit: 90, count: offices, note: "productivity + cash office" },
      { zone: "Parking / Perimeter", model: "Dahua TiOC Turret", unit: 130, count: parking ? 4 : 0, note: "full-color night + deterrence" },
    ].filter((r) => r.count > 0);

    const totalCams = rows.reduce((s, r) => s + r.count, 0);
    const camCost = rows.reduce((s, r) => s + r.count * r.unit, 0);
    const switches = Math.ceil(totalCams / 16);
    const switchCost = switches * 220;
    const nvrs = Math.ceil(totalCams / 32);
    const nvrCost = nvrs * 650;

    const bitrate = RES_BITRATE[res];
    const gbPerCamPerDay = bitrate * 10.8 * 0.6; // H.265 smart codec ~40% saving
    const storageTB = (totalCams * gbPerCamPerDay * retention) / 1024;
    const nvrDrives = Math.max(nvrs, Math.ceil(storageTB / 16));

    const hwLow = camCost + switchCost + nvrCost + nvrDrives * 260;
    const hwHigh = Math.round(hwLow * 1.35);

    return { rows, totalCams, camCost, switches, switchCost, nvrs, nvrCost, storageTB, nvrDrives, hwLow, hwHigh };
  }, [sqft, aisles, lanes, entrances, stockrooms, offices, parking, retention, res]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      {/* Inputs */}
      <div className="xl:col-span-4 bg-[#121214] border border-white/10 p-4 space-y-4">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
          <Ruler size={16} weight="bold" className="text-[#007AFF]" /> Store Parameters
        </h2>
        <NumberField label="Total Area (sqft)" value={sqft} onChange={setSqft} icon={Storefront} min={100} />
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Aisles" value={aisles} onChange={setAisles} icon={Package} />
          <NumberField label="Checkout Lanes" value={lanes} onChange={setLanes} icon={ShoppingCartSimple} />
          <NumberField label="Entrances" value={entrances} onChange={setEntrances} icon={DoorOpen} />
          <NumberField label="Stock Rooms" value={stockrooms} onChange={setStockrooms} icon={Package} />
          <NumberField label="Offices" value={offices} onChange={setOffices} icon={Buildings} />
          <NumberField label="Retention (days)" value={retention} onChange={setRetention} icon={HardDrives} min={1} />
        </div>
        <div>
          <label className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#A1A1AA] mb-2"><VideoCamera size={14} weight="bold" /> Resolution</label>
          <select data-testid="planner-res" value={res} onChange={(e) => setRes(e.target.value)}
            className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#007AFF] transition-colors">
            {Object.keys(RES_BITRATE).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
          <input type="checkbox" data-testid="planner-parking" checked={parking} onChange={(e) => setParking(e.target.checked)} className="accent-[#007AFF] w-4 h-4" />
          <Car size={16} weight="bold" /> Include parking / perimeter
        </label>
      </div>

      {/* Results */}
      <div className="xl:col-span-8 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric icon={VideoCamera} label="Total Cameras" value={plan.totalCams} accent="#007AFF" />
          <Metric icon={CurrencyDollar} label="Est. Hardware" value={`$${(plan.hwLow / 1000).toFixed(1)}k–${(plan.hwHigh / 1000).toFixed(1)}k`} accent="#32D74B" />
          <Metric icon={HardDrives} label="Storage" value={`${plan.storageTB.toFixed(1)} TB`} sub={`${retention}d retention`} accent="#FF9F0A" />
          <Metric icon={Cpu} label="Infra" value={`${plan.nvrs} NVR · ${plan.switches} PoE`} accent="#0A84FF" />
        </div>

        <div className="bg-[#121214] border border-white/10 overflow-x-auto">
          <div className="px-4 py-3 border-b border-white/10">
            <h3 className="font-display font-black uppercase tracking-tighter text-sm">Recommended Coverage Plan · {sqft.toLocaleString()} sqft</h3>
          </div>
          <table className="w-full text-sm" data-testid="planner-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#71717A] border-b border-white/10">
                <th className="px-4 py-2 font-medium">Zone</th>
                <th className="px-4 py-2 font-medium">Recommended Model</th>
                <th className="px-4 py-2 font-medium text-right">Qty</th>
                <th className="px-4 py-2 font-medium text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {plan.rows.map((r) => (
                <tr key={r.zone} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.zone}</p>
                    <p className="text-xs text-[#71717A]">{r.note}</p>
                  </td>
                  <td className="px-4 py-3 text-[#A1A1AA]">{r.model}</td>
                  <td className="px-4 py-3 text-right font-mono-data">{r.count}</td>
                  <td className="px-4 py-3 text-right font-mono-data text-[#32D74B]">${(r.count * r.unit).toLocaleString()}</td>
                </tr>
              ))}
              <tr className="border-t border-white/10 bg-white/5">
                <td className="px-4 py-3 font-display font-bold uppercase tracking-tight" colSpan={2}>Cameras Total</td>
                <td className="px-4 py-3 text-right font-mono-data font-medium">{plan.totalCams}</td>
                <td className="px-4 py-3 text-right font-mono-data font-medium text-[#32D74B]">${plan.camCost.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <InfraCard title="Recording" lines={[`${plan.nvrs}× NVR (32-ch)`, `$${plan.nvrCost.toLocaleString()}`, `${plan.nvrDrives}× surveillance HDD`]} />
          <InfraCard title="Network" lines={[`${plan.switches}× 16-port PoE switch`, `$${plan.switchCost.toLocaleString()}`, "Cat6 + UPS backup"]} />
          <InfraCard title="Storage Plan" lines={[`${plan.storageTB.toFixed(1)} TB @ ${res}`, `${retention}-day retention`, "H.265 smart codec"]} />
        </div>

        <div className="bg-[#007AFF]/10 border border-[#007AFF]/40 p-4">
          <p className="text-sm text-[#E4E4E7] leading-relaxed">
            For a <span className="font-medium">{sqft.toLocaleString()} sqft</span> store this plan puts <span className="font-medium">{plan.totalCams} cameras</span> in the highest-value zones (aisles, checkout POS, receiving dock, entrances). Prioritize <span className="font-medium">4K on aisles & checkout</span> so SentryVision can read hand movements and concealment. Estimated hardware: <span className="font-medium text-[#32D74B]">${plan.hwLow.toLocaleString()}–${plan.hwHigh.toLocaleString()}</span> before installation labor.
          </p>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-[#121214] border border-white/10 p-4" data-testid={`metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-[#71717A]">{label}</span>
        <Icon size={16} weight="bold" style={{ color: accent }} />
      </div>
      <div className="font-mono-data text-xl">{value}</div>
      {sub && <div className="text-xs text-[#71717A] mt-1 font-mono-data">{sub}</div>}
    </div>
  );
}

function InfraCard({ title, lines }) {
  return (
    <div className="bg-[#121214] border border-white/10 p-4">
      <h4 className="font-display font-black uppercase tracking-tighter text-xs text-[#A1A1AA] mb-2">{title}</h4>
      {lines.map((l, i) => (
        <p key={i} className={`font-mono-data ${i === 0 ? "text-sm text-white" : "text-xs text-[#71717A]"} mt-1`}>{l}</p>
      ))}
    </div>
  );
}
